import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  DateRangeInput,
  DateTimeRangeInput,
  ScheduleBuilderAttendanceAdjustmentRecord,
  ScheduleBuilderAttendanceRecord,
  ScheduleBuilderAuditLogQuery,
  ScheduleBuilderAuditLogRecord,
  ScheduleBuilderAvailabilityPreferenceRecord,
  ScheduleBuilderCoverageTargetInput,
  ScheduleBuilderCoverageTargetRecord,
  ScheduleBuilderEmployeeCreateInput,
  ScheduleBuilderEmployee,
  ScheduleBuilderEmployeeUpdateInput,
  ScheduleBuilderExportLogInput,
  ScheduleBuilderPublicationCreateInput,
  ScheduleBuilderProfile,
  ScheduleBuilderPublication,
  ScheduleBuilderShiftUpsertInput,
  ScheduleBuilderRoleInput,
  ScheduleBuilderRole,
  ScheduleBuilderTemplateCreateInput,
  ScheduleBuilderTemplateMeta,
  ScheduleBuilderRules,
  ScheduleBuilderSettingsRow,
  ScheduleBuilderShiftRecord,
  ScheduleBuilderShiftRequestRecord,
  ScheduleBuilderTimeOffUpsertInput,
  ScheduleBuilderTemplate,
  ScheduleBuilderTemplateItem,
  ScheduleStorageUploadBody,
  ScheduleBuilderTimeOffEntryRecord,
  ScheduleBuilderTimeOffRequestRecord,
  ScheduleBuilderAttendanceUpsertInput,
  ScheduleBuilderWorkPointInput,
  ScheduleBuilderWorkPoint,
  ScheduleCalendarDirectoryEmployee,
  ScheduleCalendarPublication,
  ScheduleCalendarShift,
  ScheduleCalendarWorkPoint,
  ScheduleCreateMyAttendanceAdjustmentRequestInput,
  ScheduleCreateMyShiftRequestInput,
  ScheduleEmployeeDirectoryColor,
  ScheduleMiniCalendarScope,
  ScheduleMiniTeamShift,
  ScheduleResolveAttendanceAdjustmentInput,
  ScheduleReviewAvailabilityDecision,
  ScheduleMyAttendance,
  ScheduleMyAttendanceAdjustmentRequest,
  ScheduleMyAvailabilityPreference,
  ScheduleMyAvailabilityUpsertInput,
  ScheduleMyEmployeeProfile,
  ScheduleMyShift,
  ScheduleMyShiftRequest,
  ScheduleSaveMyTimeOffRequestInput,
  ScheduleSaveMyTimeOffRequestResult,
  ScheduleMyTimeOffEntry,
  ScheduleMyTimeOffRequest,
  ScheduleSelfServicePort,
  ScheduleUpdateMyShiftRequestInput,
} from "@tihany/module-schedule-contracts";

type UpdateColorRpcRow = {
  color?: string | null;
};

type NameObject = { name: string };

type RawMyEmployeeProfile = Omit<ScheduleMyEmployeeProfile, "role" | "default_work_point"> & {
  role?: NameObject | NameObject[] | null;
  default_work_point?: NameObject | NameObject[] | null;
};

type RawWorkPoint = {
  id: string;
  name: string;
  color: string;
};

type RawMyShift = Omit<ScheduleMyShift, "work_point"> & {
  work_point?: RawWorkPoint | RawWorkPoint[] | null;
};

type RawShiftRequestShift = {
  starts_at: string;
  ends_at: string;
};

type RawMyShiftRequest = Omit<ScheduleMyShiftRequest, "shift"> & {
  shift?: RawShiftRequestShift | RawShiftRequestShift[] | null;
};

const toSingleNameObject = (value: NameObject | NameObject[] | null | undefined): NameObject | null => {
  if (!value) return null;
  if (Array.isArray(value)) return value[0] ?? null;
  return value;
};

const normalizeMyEmployeeProfile = (value: RawMyEmployeeProfile | null): ScheduleMyEmployeeProfile | null => {
  if (!value) return null;
  return {
    ...value,
    role: toSingleNameObject(value.role),
    default_work_point: toSingleNameObject(value.default_work_point),
  };
};

const toSingleWorkPoint = (value: RawWorkPoint | RawWorkPoint[] | null | undefined): RawWorkPoint | null => {
  if (!value) return null;
  if (Array.isArray(value)) return value[0] ?? null;
  return value;
};

const normalizeMyShift = (value: RawMyShift): ScheduleMyShift => ({
  ...value,
  work_point: toSingleWorkPoint(value.work_point),
});

const toSingleShiftRequestShift = (
  value: RawShiftRequestShift | RawShiftRequestShift[] | null | undefined,
): RawShiftRequestShift | null => {
  if (!value) return null;
  if (Array.isArray(value)) return value[0] ?? null;
  return value;
};

const normalizeMyShiftRequest = (value: RawMyShiftRequest): ScheduleMyShiftRequest => ({
  ...value,
  shift: toSingleShiftRequestShift(value.shift),
});

export const createScheduleSelfServiceSupabasePort = (
  supabase: SupabaseClient,
): ScheduleSelfServicePort => ({
  async getCurrentAuthUserId() {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();

    if (error) throw error;
    return user?.id ?? null;
  },
  async listBuilderRoles() {
    const { data, error } = await supabase
      .from("schedule_roles")
      .select("*")
      .order("sort_order")
      .order("name");

    if (error) throw error;
    return (data ?? []) as ScheduleBuilderRole[];
  },
  async listBuilderWorkPoints() {
    const { data, error } = await supabase
      .from("schedule_work_points")
      .select("*")
      .order("sort_order")
      .order("name");

    if (error) throw error;
    return (data ?? []) as ScheduleBuilderWorkPoint[];
  },
  async listBuilderEmployees() {
    const { data, error } = await supabase
      .from("schedule_employees")
      .select("*")
      .order("full_name");

    if (error) throw error;
    return (data ?? []) as ScheduleBuilderEmployee[];
  },
  async listBuilderProfiles() {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, first_name, last_name, full_name, email")
      .order("full_name");

    if (error) throw error;
    return (data ?? []) as ScheduleBuilderProfile[];
  },
  async getBuilderSettingsRow() {
    const { data, error } = await supabase
      .from("schedule_settings")
      .select("rules")
      .eq("id", 1)
      .single();

    if (error) throw error;
    return (data ?? null) as ScheduleBuilderSettingsRow | null;
  },
  async listBuilderShifts(range: DateTimeRangeInput) {
    const { data, error } = await supabase
      .from("schedule_shifts")
      .select("*, employee:schedule_employees(*), work_point:schedule_work_points(*), role:schedule_roles(*)")
      .lte("starts_at", range.endsAt)
      .gte("ends_at", range.startsAt)
      .order("starts_at", { ascending: true });

    if (error) throw error;
    return (data ?? []) as ScheduleBuilderShiftRecord[];
  },
  async listBuilderAttendance(range: DateRangeInput) {
    const { data, error } = await supabase
      .from("schedule_attendance")
      .select("*, employee:schedule_employees(*), work_point:schedule_work_points(*)")
      .gte("attendance_date", range.startsOn)
      .lte("attendance_date", range.endsOn)
      .order("attendance_date", { ascending: true });

    if (error) throw error;
    return (data ?? []) as ScheduleBuilderAttendanceRecord[];
  },
  async listBuilderAttendanceAdjustmentRequests(range: DateTimeRangeInput) {
    const { data, error } = await supabase
      .from("schedule_attendance_adjustment_requests")
      .select("*, employee:schedule_employees(*)")
      .or(`status.eq.pending,and(created_at.gte.${range.startsAt},created_at.lte.${range.endsAt})`)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return (data ?? []) as ScheduleBuilderAttendanceAdjustmentRecord[];
  },
  async listBuilderTimeOffEntries(range: DateRangeInput) {
    const { data, error } = await supabase
      .from("schedule_time_off")
      .select("*, employee:schedule_employees(*)")
      .lte("starts_on", range.endsOn)
      .gte("ends_on", range.startsOn)
      .order("starts_on", { ascending: true });

    if (error) throw error;
    return (data ?? []) as ScheduleBuilderTimeOffEntryRecord[];
  },
  async listBuilderTimeOffRequests() {
    const { data, error } = await supabase
      .from("schedule_time_off_requests")
      .select("*, employee:schedule_employees(*)")
      .order("created_at", { ascending: false });

    if (error) throw error;
    return (data ?? []) as ScheduleBuilderTimeOffRequestRecord[];
  },
  async listBuilderAvailabilityPreferences(range: DateRangeInput) {
    const { data, error } = await supabase
      .from("schedule_availability_preferences")
      .select("*, employee:schedule_employees(*)")
      .gte("preference_date", range.startsOn)
      .lte("preference_date", range.endsOn)
      .order("preference_date", { ascending: true });

    if (error) throw error;
    return (data ?? []) as ScheduleBuilderAvailabilityPreferenceRecord[];
  },
  async listBuilderShiftRequests() {
    const { data, error } = await supabase
      .from("schedule_shift_requests")
      .select(
        "id, employee_id, shift_id, target_employee_id, target_shift_id, request_type, requested_date, notes, status, decided_by, decided_at, created_at",
      )
      .order("created_at", { ascending: false });

    if (error) throw error;
    return (data ?? []) as ScheduleBuilderShiftRequestRecord[];
  },
  async listBuilderShiftsByIds(shiftIds: string[]) {
    if (shiftIds.length === 0) return [];

    const { data, error } = await supabase
      .from("schedule_shifts")
      .select("id, employee_id, work_point_id, role_id, starts_at, ends_at, notes, rules_override, override_reason")
      .in("id", shiftIds);

    if (error) throw error;
    return (data ?? []) as ScheduleBuilderShiftRecord[];
  },
  async listBuilderCoverageTargets(range: DateRangeInput) {
    const { data, error } = await supabase
      .from("schedule_coverage_targets")
      .select("*, work_point:schedule_work_points(*), role:schedule_roles(*)")
      .gte("target_date", range.startsOn)
      .lte("target_date", range.endsOn)
      .order("target_date", { ascending: true });

    if (error) throw error;
    return (data ?? []) as ScheduleBuilderCoverageTargetRecord[];
  },
  async listBuilderAuditLogs(input: ScheduleBuilderAuditLogQuery) {
    let query = supabase
      .from("schedule_audit_log")
      .select("id, actor_user_id, actor_employee_id, entity_type, entity_id, action, payload, created_at, actor:schedule_employees(full_name)")
      .order("created_at", { ascending: false })
      .limit(input.limit ?? 200);

    if (input.entityType && input.entityType !== "all") {
      query = query.eq("entity_type", input.entityType);
    }
    if (input.action && input.action !== "all") {
      query = query.eq("action", input.action);
    }
    if (input.startDate) {
      query = query.gte("created_at", `${input.startDate}T00:00:00`);
    }
    if (input.endDate) {
      query = query.lte("created_at", `${input.endDate}T23:59:59`);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as ScheduleBuilderAuditLogRecord[];
  },
  async createBuilderCoverageTarget(input: ScheduleBuilderCoverageTargetInput) {
    const { error } = await supabase.from("schedule_coverage_targets").insert(input);
    if (error) throw error;
  },
  async updateBuilderCoverageTarget(targetId: string, input: ScheduleBuilderCoverageTargetInput) {
    const { error } = await supabase.from("schedule_coverage_targets").update(input).eq("id", targetId);
    if (error) throw error;
  },
  async deleteBuilderCoverageTarget(targetId: string) {
    const { error } = await supabase.from("schedule_coverage_targets").delete().eq("id", targetId);
    if (error) throw error;
  },
  async createBuilderRole(input: ScheduleBuilderRoleInput) {
    const { error } = await supabase.from("schedule_roles").insert(input);
    if (error) throw error;
  },
  async updateBuilderRole(roleId: string, input: ScheduleBuilderRoleInput) {
    const { error } = await supabase.from("schedule_roles").update(input).eq("id", roleId);
    if (error) throw error;
  },
  async deleteBuilderRole(roleId: string) {
    const { error } = await supabase.from("schedule_roles").delete().eq("id", roleId);
    if (error) throw error;
  },
  async setBuilderRoleActive(roleId: string, isActive: boolean) {
    const { error } = await supabase.from("schedule_roles").update({ is_active: isActive }).eq("id", roleId);
    if (error) throw error;
  },
  async createBuilderWorkPoint(input: ScheduleBuilderWorkPointInput) {
    const { error } = await supabase.from("schedule_work_points").insert(input);
    if (error) throw error;
  },
  async updateBuilderWorkPoint(pointId: string, input: ScheduleBuilderWorkPointInput) {
    const { error } = await supabase.from("schedule_work_points").update(input).eq("id", pointId);
    if (error) throw error;
  },
  async setBuilderWorkPointActive(pointId: string, isActive: boolean) {
    const { error } = await supabase
      .from("schedule_work_points")
      .update({ is_active: isActive })
      .eq("id", pointId);
    if (error) throw error;
  },
  async createBuilderEmployee(input: ScheduleBuilderEmployeeCreateInput) {
    const { error } = await supabase.from("schedule_employees").insert(input);
    if (error) throw error;
  },
  async updateBuilderEmployee(employeeId: string, input: ScheduleBuilderEmployeeUpdateInput) {
    const { error } = await supabase.from("schedule_employees").update(input).eq("id", employeeId);
    if (error) throw error;
  },
  async setBuilderEmployeeActive(employeeId: string, isActive: boolean) {
    const { error } = await supabase
      .from("schedule_employees")
      .update({ is_active: isActive })
      .eq("id", employeeId);
    if (error) throw error;
  },
  async createBuilderPublication(input: ScheduleBuilderPublicationCreateInput) {
    const { error } = await supabase.from("schedule_publications").insert(input);
    if (error) throw error;
  },
  async setBuilderPublicationLocked(publicationId: string, locked: boolean) {
    const { error } = await supabase.from("schedule_publications").update({ locked }).eq("id", publicationId);
    if (error) throw error;
  },
  async deleteBuilderPublication(publicationId: string) {
    const { error } = await supabase.from("schedule_publications").delete().eq("id", publicationId);
    if (error) throw error;
  },
  async updateBuilderRules(rules: ScheduleBuilderRules) {
    const { error } = await supabase.from("schedule_settings").update({ rules }).eq("id", 1);
    if (error) throw error;
  },
  async reviewAvailabilityPreference(preferenceId: string, decision: ScheduleReviewAvailabilityDecision, reviewNote?: string | null) {
    const { error } = await supabase.rpc("review_availability_preference_tx", {
      p_preference_id: preferenceId,
      p_decision: decision,
      p_review_note: reviewNote ?? null,
    });
    if (error) throw error;
  },
  async upsertBuilderShift(shiftId: string | null, input: ScheduleBuilderShiftUpsertInput) {
    const query = shiftId
      ? supabase.from("schedule_shifts").update(input).eq("id", shiftId)
      : supabase.from("schedule_shifts").insert(input);
    const { error } = await query;
    if (error) throw error;
  },
  async updateBuilderShiftTimes(shiftId: string, startsAt: string, endsAt: string) {
    const { error } = await supabase
      .from("schedule_shifts")
      .update({ starts_at: startsAt, ends_at: endsAt })
      .eq("id", shiftId);
    if (error) throw error;
  },
  async deleteBuilderShift(shiftId: string) {
    const { error } = await supabase.from("schedule_shifts").delete().eq("id", shiftId);
    if (error) throw error;
  },
  async insertBuilderShifts(shifts: ScheduleBuilderShiftUpsertInput[]) {
    if (shifts.length === 0) return;
    const { error } = await supabase.from("schedule_shifts").insert(shifts);
    if (error) throw error;
  },
  async upsertBuilderAttendance(attendanceId: string | null, input: ScheduleBuilderAttendanceUpsertInput) {
    const query = attendanceId
      ? supabase.from("schedule_attendance").update(input).eq("id", attendanceId)
      : supabase.from("schedule_attendance").insert(input);
    const { error } = await query;
    if (error) throw error;
  },
  async deleteBuilderAttendance(attendanceId: string) {
    const { error } = await supabase.from("schedule_attendance").delete().eq("id", attendanceId);
    if (error) throw error;
  },
  async updateBuilderAttendanceActualEnd(attendanceId: string, actualEnd: string) {
    const { error } = await supabase
      .from("schedule_attendance")
      .update({ actual_end: actualEnd })
      .eq("id", attendanceId);
    if (error) throw error;
  },
  async resolveBuilderAttendanceAdjustment(input: ScheduleResolveAttendanceAdjustmentInput) {
    const { error } = await supabase.rpc("resolve_attendance_adjustment_request_tx", {
      p_request_id: input.requestId,
      p_decision: input.decision,
      p_decision_note: input.decisionNote ?? null,
    });
    if (error) throw error;
  },
  async upsertBuilderTimeOff(timeOffId: string | null, input: ScheduleBuilderTimeOffUpsertInput) {
    const query = timeOffId
      ? supabase.from("schedule_time_off").update(input).eq("id", timeOffId)
      : supabase.from("schedule_time_off").insert(input);
    const { error } = await query;
    if (error) throw error;
  },
  async deleteBuilderTimeOff(timeOffId: string) {
    const { error } = await supabase.from("schedule_time_off").delete().eq("id", timeOffId);
    if (error) throw error;
  },
  async approveBuilderTimeOffRequest(requestId: string) {
    const { error } = await supabase.rpc("approve_time_off_request_tx", {
      p_request_id: requestId,
    });
    if (error) throw error;
  },
  async setBuilderTimeOffRequestDecision(
    requestId: string,
    status: "approved" | "rejected" | "cancelled" | "pending",
    _decidedBy: string | null,
    _decidedAt: string,
  ) {
    if (status !== "approved" && status !== "rejected") {
      throw new Error("Only approved/rejected reviewer decisions are supported by this action.");
    }

    const { error } = await supabase.rpc("resolve_time_off_request_tx" as never, {
      p_request_id: requestId,
      p_decision: status,
    } as never);
    if (error) throw error;
  },
  async setBuilderShiftRequestDecision(
    requestId: string,
    status: "approved" | "rejected" | "cancelled" | "pending",
    _decidedBy: string | null,
    _decidedAt: string,
  ) {
    if (status !== "approved" && status !== "rejected") {
      throw new Error("Only approved/rejected reviewer decisions are supported by this action.");
    }

    const { error } = await supabase.rpc("resolve_shift_request_tx" as never, {
      p_request_id: requestId,
      p_decision: status,
    } as never);
    if (error) throw error;
  },
  async swapBuilderShiftAssignments(shiftId: string, targetShiftId: string) {
    const { error } = await supabase.rpc("swap_schedule_shift_assignments", {
      p_shift_id: shiftId,
      p_target_shift_id: targetShiftId,
    });
    if (error) throw error;
  },
  async logBuilderExportAction(input: ScheduleBuilderExportLogInput) {
    const { error } = await supabase.rpc("log_schedule_export_action_tx", {
      p_export_kind: input.exportKind,
      p_period_start: input.periodStart,
      p_period_end: input.periodEnd,
      p_employee_id: input.employeeId ?? null,
      p_work_point_id: input.workPointId ?? null,
      p_meta: input.meta ?? {},
    });
    if (error) throw error;
  },
  async createBuilderTemplate(input: ScheduleBuilderTemplateCreateInput) {
    const { data, error } = await supabase
      .from("schedule_shift_templates")
      .insert(input)
      .select("*")
      .single();
    if (error) throw error;
    if (!data) throw new Error("schedule_shift_templates insert returned no data");
    return data as ScheduleBuilderTemplate;
  },
  async createBuilderTemplateItems(items: Omit<ScheduleBuilderTemplateItem, "id">[]) {
    if (items.length === 0) return;
    const { error } = await supabase.from("schedule_shift_template_items").insert(items);
    if (error) throw error;
  },
  async getBuilderTemplateMeta(templateId: string) {
    const { data, error } = await supabase
      .from("schedule_shift_templates")
      .select("id, scope, is_active")
      .eq("id", templateId)
      .single();
    if (error) throw error;
    return (data ?? null) as ScheduleBuilderTemplateMeta | null;
  },
  async setBuilderTemplateActive(templateId: string, isActive: boolean) {
    const { error } = await supabase
      .from("schedule_shift_templates")
      .update({ is_active: isActive })
      .eq("id", templateId);
    if (error) throw error;
  },
  async deleteBuilderTemplate(templateId: string) {
    const { error } = await supabase.from("schedule_shift_templates").delete().eq("id", templateId);
    if (error) throw error;
  },
  async uploadBuilderEmployeePhoto(storagePath: string, file: ScheduleStorageUploadBody, contentType: string) {
    const { error } = await supabase.storage.from("employee-photos").upload(storagePath, file, {
      upsert: true,
      cacheControl: "3600",
      contentType,
    });
    if (error) throw error;
  },
  getBuilderEmployeePhotoPublicUrl(storagePath: string) {
    const { data } = supabase.storage.from("employee-photos").getPublicUrl(storagePath);
    return data.publicUrl;
  },
  async listBuilderPublications(range: DateRangeInput) {
    const { data, error } = await supabase
      .from("schedule_publications")
      .select("*")
      .lte("starts_on", range.endsOn)
      .gte("ends_on", range.startsOn)
      .order("starts_on", { ascending: true });

    if (error) throw error;
    return (data ?? []) as ScheduleBuilderPublication[];
  },
  async listBuilderTemplates() {
    const { data, error } = await supabase
      .from("schedule_shift_templates")
      .select("*")
      .order("name");

    if (error) throw error;
    return (data ?? []) as ScheduleBuilderTemplate[];
  },
  async listBuilderTemplateItems(templateId: string) {
    const { data, error } = await supabase
      .from("schedule_shift_template_items")
      .select("*")
      .eq("template_id", templateId)
      .order("day_of_week", { ascending: true })
      .order("start_time", { ascending: true });

    if (error) throw error;
    return (data ?? []) as ScheduleBuilderTemplateItem[];
  },
  async getEmployeeIdByAuthUserId(authUserId: string) {
    const { data, error } = await supabase
      .from("schedule_employees")
      .select("id")
      .eq("auth_user_id", authUserId)
      .maybeSingle();

    if (error) throw error;
    return data?.id ?? null;
  },
  async getMiniCalendarScopePreference(userId: string) {
    const { data, error } = await supabase
      .from("schedule_notification_settings")
      .select("mini_calendar_scope")
      .eq("user_id", userId)
      .maybeSingle();

    if (error) throw error;

    const scope = data?.mini_calendar_scope;
    return scope === "team" || scope === "self" ? (scope as ScheduleMiniCalendarScope) : null;
  },
  async saveMiniCalendarScopePreference(userId: string, scope: ScheduleMiniCalendarScope) {
    const { error } = await supabase
      .from("schedule_notification_settings")
      .upsert(
        {
          user_id: userId,
          mini_calendar_scope: scope,
        },
        { onConflict: "user_id" },
      );

    if (error) throw error;
  },
  async listMiniTeamShiftsForRange(range: DateRangeInput) {
    const { data, error } = await supabase.rpc("get_team_shifts_for_range", {
      p_start: range.startsOn,
      p_end: range.endsOn,
    });

    if (error) throw error;
    return (data ?? []) as ScheduleMiniTeamShift[];
  },
  async listMiniTeamShiftsForDay(day: string) {
    const { data, error } = await supabase.rpc("get_team_shifts_for_day", {
      p_day: day,
    });

    if (error) throw error;
    return (data ?? []) as ScheduleMiniTeamShift[];
  },
  async listActiveEmployeeDirectoryColors() {
    const { data, error } = await supabase
      .from("schedule_employee_directory")
      .select("employee_id,color")
      .eq("is_active", true);

    if (error) throw error;
    return (data ?? []) as ScheduleEmployeeDirectoryColor[];
  },
  async getMyEmployeeProfile(authUserId: string) {
    const { data, error } = await supabase
      .from("schedule_employees")
      .select(
        "id, first_name, last_name, full_name, color, avatar_url, employment_type, contract_hours_per_week, contract_hours_per_day, holiday_allowance_hours, sick_allowance_hours, overtime_limit_hours, permissions, role:schedule_roles(name), default_work_point:schedule_work_points(name)",
      )
      .eq("auth_user_id", authUserId)
      .maybeSingle();

    if (error) throw error;
    return normalizeMyEmployeeProfile((data ?? null) as RawMyEmployeeProfile | null);
  },
  async listMyShifts(input) {
    const { data, error } = await supabase
      .from("schedule_shifts")
      .select("id, starts_at, ends_at, work_point:schedule_work_points(id, name, color)")
      .eq("employee_id", input.employeeId)
      .gte("starts_at", input.range.startsAt)
      .lte("starts_at", input.range.endsAt)
      .order("starts_at", { ascending: true });

    if (error) throw error;
    return ((data ?? []) as RawMyShift[]).map(normalizeMyShift);
  },
  async listCalendarShifts(range: DateTimeRangeInput) {
    const { data, error } = await supabase
      .from("schedule_shifts")
      .select("id, employee_id, work_point_id, role_id, starts_at, ends_at, notes")
      .lte("starts_at", range.endsAt)
      .gte("ends_at", range.startsAt)
      .order("starts_at", { ascending: true });

    if (error) throw error;
    return (data ?? []) as ScheduleCalendarShift[];
  },
  async listEmployeeDirectory() {
    const { data, error } = await supabase
      .from("schedule_employee_directory")
      .select("employee_id, first_name, last_name, full_name, role_id, default_work_point_id, color, avatar_url, is_active, status")
      .order("full_name");

    if (error) throw error;
    return (data ?? []) as ScheduleCalendarDirectoryEmployee[];
  },
  async listCalendarWorkPoints() {
    const { data, error } = await supabase
      .from("schedule_work_points")
      .select("id, name, color, is_active")
      .order("sort_order")
      .order("name");

    if (error) throw error;
    return (data ?? []) as ScheduleCalendarWorkPoint[];
  },
  async listCalendarPublications(range: DateRangeInput) {
    const { data, error } = await supabase
      .from("schedule_publications")
      .select("id, starts_on, ends_on, status, message, locked")
      .lte("starts_on", range.endsOn)
      .gte("ends_on", range.startsOn)
      .order("starts_on", { ascending: true });

    if (error) throw error;
    return (data ?? []) as ScheduleCalendarPublication[];
  },
  async listMyAttendance(input) {
    const { data, error } = await supabase
      .from("schedule_attendance")
      .select("id, shift_id, attendance_date, actual_start, actual_end, status")
      .eq("employee_id", input.employeeId)
      .gte("attendance_date", input.range.startsOn)
      .lte("attendance_date", input.range.endsOn)
      .order("attendance_date", { ascending: true });

    if (error) throw error;
    return (data ?? []) as ScheduleMyAttendance[];
  },
  async upsertMyAttendance(input) {
    const payload = {
      employee_id: input.employee_id,
      shift_id: input.shift_id,
      work_point_id: input.work_point_id,
      attendance_date: input.attendance_date,
      status: input.status,
      actual_start: input.actual_start,
      actual_end: input.actual_end,
    };

    const query = input.attendanceId
      ? supabase.from("schedule_attendance").update(payload).eq("id", input.attendanceId)
      : supabase.from("schedule_attendance").insert(payload);

    const { error } = await query;
    if (error) throw error;
  },
  async listMyTimeOffEntries(input) {
    const { data, error } = await supabase
      .from("schedule_time_off")
      .select("id, type, starts_on, ends_on, hours")
      .eq("employee_id", input.employeeId)
      .lte("starts_on", input.range.endsOn)
      .gte("ends_on", input.range.startsOn)
      .order("starts_on", { ascending: true });

    if (error) throw error;
    return (data ?? []) as ScheduleMyTimeOffEntry[];
  },
  async listMyTimeOffRequests(employeeId) {
    const { data, error } = await supabase
      .from("schedule_time_off_requests")
      .select("id, type, starts_on, ends_on, hours, notes, status, created_at")
      .eq("employee_id", employeeId)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return (data ?? []) as ScheduleMyTimeOffRequest[];
  },
  async saveMyTimeOffRequest(input: ScheduleSaveMyTimeOffRequestInput) {
    const { data, error } = await supabase.rpc("save_time_off_request_tx", {
      p_request_id: input.requestId ?? null,
      p_employee_id: input.employeeId,
      p_type: input.type,
      p_starts_on: input.startsOn,
      p_ends_on: input.endsOn,
      p_hours: input.hours,
      p_notes: input.notes,
    });

    if (error) throw error;

    const row = Array.isArray(data)
      ? ((data[0] ?? null) as ScheduleSaveMyTimeOffRequestResult | null)
      : null;
    return row;
  },
  async cancelMyTimeOffRequest(requestId: string) {
    const { error } = await supabase
      .from("schedule_time_off_requests")
      .update({ status: "cancelled" })
      .eq("id", requestId);

    if (error) throw error;
  },
  async listMyShiftRequests(employeeId) {
    const { data, error } = await supabase
      .from("schedule_shift_requests")
      .select(
        "id, request_type, requested_date, notes, status, created_at, shift:schedule_shifts!schedule_shift_requests_shift_id_fkey(starts_at, ends_at)",
      )
      .eq("employee_id", employeeId)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return ((data ?? []) as RawMyShiftRequest[]).map(normalizeMyShiftRequest);
  },
  async createMyShiftRequest(input: ScheduleCreateMyShiftRequestInput) {
    const { error } = await supabase.from("schedule_shift_requests").insert(input);
    if (error) throw error;
  },
  async updateMyShiftRequest(input: ScheduleUpdateMyShiftRequestInput) {
    const { error } = await supabase
      .from("schedule_shift_requests")
      .update({
        requested_date: input.requestedDate,
        notes: input.notes,
      })
      .eq("id", input.requestId);

    if (error) throw error;
  },
  async cancelMyShiftRequest(requestId: string) {
    const { error } = await supabase
      .from("schedule_shift_requests")
      .update({ status: "cancelled" })
      .eq("id", requestId);

    if (error) throw error;
  },
  async listMyAvailabilityPreferences(input) {
    const { data, error } = await supabase
      .from("schedule_availability_preferences")
      .select("id, preference_date, status, notes, review_state")
      .eq("employee_id", input.employeeId)
      .gte("preference_date", input.range.startsOn)
      .lte("preference_date", input.range.endsOn)
      .order("preference_date", { ascending: true });

    if (error) throw error;
    return (data ?? []) as ScheduleMyAvailabilityPreference[];
  },
  async upsertMyAvailabilityPreferences(entries: ScheduleMyAvailabilityUpsertInput[]) {
    if (entries.length === 0) return;

    const { error } = await supabase
      .from("schedule_availability_preferences")
      .upsert(entries, { onConflict: "employee_id,preference_date" });

    if (error) throw error;
  },
  async deleteMyAvailabilityPreferences(employeeId: string, dates: string[]) {
    if (dates.length === 0) return;

    const { error } = await supabase
      .from("schedule_availability_preferences")
      .delete()
      .eq("employee_id", employeeId)
      .in("preference_date", dates);

    if (error) throw error;
  },
  async listMyAttendanceAdjustmentRequests(employeeId) {
    const { data, error } = await supabase
      .from("schedule_attendance_adjustment_requests")
      .select(
        "id, attendance_id, employee_id, requested_start, requested_end, requested_status, reason, status, decision_note, decided_at, created_at",
      )
      .eq("employee_id", employeeId)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return (data ?? []) as ScheduleMyAttendanceAdjustmentRequest[];
  },
  async createMyAttendanceAdjustmentRequest(input: ScheduleCreateMyAttendanceAdjustmentRequestInput) {
    const { data, error } = await supabase
      .from("schedule_attendance_adjustment_requests")
      .insert(input)
      .select(
        "id, attendance_id, employee_id, requested_start, requested_end, requested_status, reason, status, decision_note, decided_at, created_at",
      )
      .single();

    if (error) throw error;
    if (!data) throw new Error("schedule_attendance_adjustment_requests insert returned no data");
    return data as ScheduleMyAttendanceAdjustmentRequest;
  },
  async cancelMyAttendanceAdjustmentRequest(requestId: string) {
    const { error } = await supabase
      .from("schedule_attendance_adjustment_requests")
      .update({ status: "cancelled" })
      .eq("id", requestId);

    if (error) throw error;
  },
  async updateMyEmployeeColor(color: string) {
    const { data, error } = await supabase.rpc("update_my_employee_color", {
      p_color: color,
    });

    if (error) throw error;

    const row = Array.isArray(data) ? ((data[0] ?? null) as UpdateColorRpcRow | null) : null;
    return {
      color: row?.color ?? color,
    };
  },
});
